import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook to handle retrieving and processing converted job data from localStorage
 *
 * @returns {Object} Object containing the converted job data and hasJobData flag
 */
export default function useConvertedJobData() {
  const [convertedJobData, setConvertedJobDataInternal] = useState<any>(null);
  const [hasJobData, setHasJobDataInternal] = useState<boolean>(false);

  // Memoized function to process data string and update state
  const processAndSetData = useCallback((dataString: string | null) => {
    if (dataString) {
      try {
        const parsedData = JSON.parse(dataString);

        // Initial check: if parsedData is fundamentally empty or not an object, treat as no data.
        if (
          typeof parsedData !== "object" ||
          parsedData === null ||
          (typeof parsedData === "object" && Object.keys(parsedData).length === 0)
        ) {
          setConvertedJobDataInternal(null);
          setHasJobDataInternal(false);
          return;
        }

        // Prefer .formData if present (from modal flow), else use raw data (from direct AI result)
        let formDataToSet = parsedData.formData || parsedData;

        // Ensure formDataToSet is a valid object and initialize overview
        if (typeof formDataToSet === "object" && formDataToSet !== null) {
          if (typeof formDataToSet.overview === "undefined" || formDataToSet.overview === null) {
            formDataToSet.overview = "";
          }
          setConvertedJobDataInternal(formDataToSet);
          // Only set hasJobData to true if formDataToSet (after potentially picking .formData)
          // is also a non-empty object. This handles cases where parsedData might be non-empty
          // but parsedData.formData is an empty object.
          if (Object.keys(formDataToSet).length > 0) {
            setHasJobDataInternal(true);
          } else {
            setHasJobDataInternal(false);
            // If formDataToSet (e.g. from parsedData.formData) is an empty object, treat as no job data.
            // We still setConvertedJobDataInternal above with the (potentially empty) object for inspection if needed.
          }
        } else {
          // If formDataToSet ended up not being a usable object (e.g., parsedData was a primitive after the || operator)
          setConvertedJobDataInternal({ overview: "" }); // Minimal default
          setHasJobDataInternal(false); // Not actual job data for navigation purposes
        }
      } catch (error) {
        // JSON.parse failed or other runtime error during processing
        console.error("useConvertedJobData: Error parsing or processing job data:", error);
        localStorage.removeItem("convertedJobData"); // Clear problematic data
        setConvertedJobDataInternal(null);
        setHasJobDataInternal(false);
      }
    } else {
      setConvertedJobDataInternal(null);
      setHasJobDataInternal(false);
    }
  }, []); // Empty dependency array for useCallback as it doesn't close over reactive values

  // Effect for initial load from localStorage and for listening to storage events
  useEffect(() => {
    // Initial load
    const initialStoredData = localStorage.getItem("convertedJobData");
    processAndSetData(initialStoredData);

    // Listener for storage changes from other tabs/windows or direct localStorage manipulation
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "convertedJobData") {
        processAndSetData(event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [processAndSetData]); // Depend on the memoized processAndSetData function

  return { convertedJobData, hasJobData };
}
